/**
 * useOwnedFrozenArtworks — read-only discovery of the connected wallet's FROZEN
 * artworks that are currently PLACED on land inside this Territory.
 *
 * A frozen artwork leaves the mutable Canvas draft (#14 law) but, while still
 * owned by the creator, it must remain VISIBLE inside their Canvas as a
 * read-only reference so they can arrange other modules around it. Placement is
 * mutable; opening the PlacementEditor from the Canvas lets the owner drag it.
 *
 * All data is backend-authoritative (`artworkApi.selfCollection` + `getArtwork`).
 * Nothing is fabricated. Items whose placement bbox does NOT intersect the
 * territory parcels are omitted (they live elsewhere on the Grid).
 */
import { useCallback, useEffect, useState } from "react";
import { artworkApi, type PixelContent } from "../../lib/artwork-api";
import type { TerritoryGeometry } from "../../domain/canvas-model";

export interface PlacedFrozenArtwork {
  artwork_id: string;
  title: string;
  content: PixelContent;
  width: number;
  height: number;
  anchor_x: number;
  anchor_y: number;
  rotation: number; // degrees
  background: string | null;
  emptyOpacityBps: number;
}

function bboxIntersectsTerritory(
  ax: number, ay: number, w: number, h: number, territory?: TerritoryGeometry,
): boolean {
  if (!territory) return true;
  const x1 = ax + w, y1 = ay + h;
  return territory.parcels.some((p) => ax < p.x + p.w && x1 > p.x && ay < p.y + p.h && y1 > p.y);
}

export function useOwnedFrozenArtworks(territory: TerritoryGeometry | undefined, enabled: boolean) {
  const [items, setItems] = useState<PlacedFrozenArtwork[]>([]);
  const [tick, setTick] = useState(0);
  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled) { setItems([]); return; }
    const c = new AbortController();
    (async () => {
      try {
        const coll = await artworkApi.selfCollection(c.signal);
        if (c.signal.aborted) return;
        const ids = Array.from(new Set([...(coll.created_by ?? []), ...(coll.owned ?? [])].map((a) => a.artwork_id)));
        const full = await Promise.all(
          ids.map((id) => artworkApi.getArtwork(id, c.signal).catch(() => null)),
        );
        if (c.signal.aborted) return;
        const placed: PlacedFrozenArtwork[] = [];
        for (const a of full) {
          if (!a || !a.content || !a.placement) continue;
          const p = a.placement;
          if (!bboxIntersectsTerritory(p.anchor_x, p.anchor_y, a.width, a.height, territory)) continue;
          placed.push({
            artwork_id: a.artwork_id,
            title: a.title || "Untitled",
            content: a.content,
            width: a.width,
            height: a.height,
            anchor_x: p.anchor_x,
            anchor_y: p.anchor_y,
            rotation: (p.rotation_millideg ?? p.rotation * 1000) / 1000,
            background: p.background ?? null,
            emptyOpacityBps: p.empty_opacity_bps ?? 0,
          });
        }
        setItems(placed);
      } catch {
        /* discovery is advisory — never blocks the editor */
      }
    })();
    return () => c.abort();
  }, [territory, enabled, tick]);

  return { items, refetch };
}
